import React from 'react';
import { Link } from 'react-router-dom';

interface CustomLinkProps {
  to: string;
  children: React.ReactNode;
  external?: boolean;
  download?: string;
  // True when the child is already a real button (its own background +
  // hover state, e.g. the "Download CV" ink-stamp button) rather than
  // in-prose text — skips the font-bold class the circle-hover CSS keys
  // off of, so a button never gets the link affordance stacked on top of
  // its own.
  asButton?: boolean;
}

// Best-effort plain text extraction, used only to scale down the marker
// band's rotation for longer link text (see --link-marker-rotate below) —
// a fixed rotation angle is a barely-there tilt on a short word, but the
// same angle over a long sentence's width translates into real vertical
// rise/fall at each end, reading as a slant rather than a hand-placed tilt.
function getPlainText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getPlainText).join('');
  if (React.isValidElement(node)) return getPlainText((node.props as { children?: React.ReactNode }).children);
  return '';
}

const CustomLink: React.FC<CustomLinkProps> = ({ to, children, external = false, download, asButton = false}) => {
  const baseClasses = asButton
    ? 'relative inline-block transition-colors duration-300'
    : 'relative inline-block font-bold transition-colors duration-300';
  const hoverClasses = "";
  const afterClasses = asButton
    ? ''
    : "after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-current after:left-0 after:bottom-0 after:transform after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100";

  // -1.2deg at 6 characters or fewer, easing down much faster than the
  // first pass — flat to -0.1deg by ~40 characters, since even a
  // moderate-length sentence link was still reading as a diagonal slant.
  const linkLength = getPlainText(children).trim().length;
  const markerRotateDeg = asButton
    ? -1.2
    : -Math.max(0.1, 1.2 - Math.max(0, linkLength - 6) * 0.035);
  const markerStyle = asButton
    ? {}
    : ({ '--link-marker-rotate': `${markerRotateDeg}deg` } as React.CSSProperties);

  const className = `${baseClasses} ${hoverClasses} ${afterClasses}`;
  const style = { color: 'var(--color-alt-accent)', ...markerStyle };

  // A download is a real link to the file with the `download` attribute
  // (works without JS, can be copied, is crawlable), in the same tab.
  if (download) {
    return (
      <a href={to} download={download} className={className} style={style}>
        {children}
      </a>
    );
  }

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} style={style}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className} style={style}>
      {children}
    </Link>
  );
};

export default CustomLink;